import { List, useTable } from "@refinedev/antd";
import { Table, Tag, Space, Typography, Progress, Tooltip } from "antd";
import type { NodeListItem } from "../../types";
import { fmtDateTime } from "../../utils";

const HEALTH_COLOR: Record<string, string> = {
  healthy:  "green",
  degraded: "orange",
  down:     "red",
  unknown:  "default",
};

export const NodeList = () => {
  const { tableProps } = useTable<NodeListItem>({ syncWithLocation: true });

  return (
    <List title="VPN-ноды" breadcrumb={false}>
      <Table {...tableProps} rowKey="id">
        <Table.Column dataIndex="id" title="ID" width={60} />
        <Table.Column<NodeListItem> title="Имя"
          render={(_, r) => (
            <Space size={4} wrap>
              <Typography.Text strong>{r.name}</Typography.Text>
              {r.legacy_imported && (
                <Tooltip title="Legacy-нода: клиенты хранятся в локальной 3X-UI БД, master не управляет ими (read-only).">
                  <Tag color="geekblue">legacy</Tag>
                </Tooltip>
              )}
              {r.node_class === "premium" && <Tag color="gold">premium</Tag>}
            </Space>
          )} />
        <Table.Column dataIndex="public_ip" title="IP"
          render={(v: string) => <Typography.Text code copyable>{v}</Typography.Text>} />
        <Table.Column dataIndex="location" title="Локация" />
        <Table.Column<NodeListItem>
          title="Приоритет"
          render={(_, r) => (
            <Tooltip title="Меньшее значение = выше приоритет. Sing-box (Karing/Hiddify) пробует ноды строго в этом порядке. Legacy-ноды стоят 900 — никогда не первые.">
              <Tag color={r.priority <= 50 ? "green" : r.priority >= 500 ? "default" : "blue"}>
                {r.priority}
              </Tag>
            </Tooltip>
          )}
        />
        <Table.Column<NodeListItem>
          title="Health"
          render={(_, r) => (
            <Tooltip title={r.health_checked_at
              ? `Последняя проверка: ${fmtDateTime(r.health_checked_at)}`
              : "Не проверялось"}>
              <Tag color={HEALTH_COLOR[r.health_status] ?? "default"}>
                {r.health_status ?? "unknown"}
              </Tag>
            </Tooltip>
          )}
        />
        <Table.Column<NodeListItem>
          title="Тарифы"
          render={(_, r) => (
            <Space size={4} wrap>
              {r.serves_basic && <Tag color="blue">basic</Tag>}
              {r.serves_parental && <Tag color="purple">parental</Tag>}
              {r.serves_premium && <Tag color="gold">premium</Tag>}
            </Space>
          )}
        />
        <Table.Column<NodeListItem>
          title="Заполненность"
          render={(_, r) => {
            // Для legacy capacity = capacity_basic (мы зашили туда фактическое
            // число клиентов при регистрации, 50 для Aeza / 10 для cpx22).
            // Для production — сумма capacity по обслуживаемым тарифам.
            const cap = r.legacy_imported
              ? r.capacity_basic
              : (r.serves_basic ? r.capacity_basic : 0)
                + (r.serves_parental ? r.capacity_parental : 0)
                + (r.serves_premium ? r.capacity_premium : 0);
            const pct = cap > 0 ? (r.active_subs / cap) * 100 : 0;
            return (
              <Space direction="vertical" size={0} style={{ minWidth: 160 }}>
                <Progress percent={Math.round(pct)} size="small"
                  status={pct >= 90 ? "exception" : pct >= 75 ? "active" : "normal"} />
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {r.active_subs} / {cap}
                </Typography.Text>
              </Space>
            );
          }}
        />
        <Table.Column<NodeListItem>
          dataIndex="online_now" title="Online сейчас"
          render={(v: number) => v > 0
            ? <Tag color="green">{v}</Tag>
            : <Tag>0</Tag>}
        />
        <Table.Column dataIndex="last_sample_at" title="Последний sample"
          render={fmtDateTime} />
        <Table.Column<NodeListItem>
          title="Активность"
          render={(_, r) => r.active
            ? <Tag color="green">активна</Tag>
            : <Tag>выключена</Tag>}
        />
      </Table>
    </List>
  );
};
